import { useMemo, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxGroupLabel,
  ComboboxInput,
  ComboboxInputGroup,
  ComboboxItem,
  ComboboxList,
} from '@/components/ui/combobox';

import {
  CLAUDE_MODEL_CATALOG,
  type ClaudeModelEntry,
  claudeModelName,
} from '../claudeModelCatalog';

/** All catalog entries, flattened, for exact-match and lookup checks. */
const ALL_MODELS: readonly ClaudeModelEntry[] = CLAUDE_MODEL_CATALOG.flatMap(
  (tier) => tier.models,
);

/** Case-insensitive contains, against both the friendly name and the id (FR-004). */
function matches(
  entries: readonly ClaudeModelEntry[],
  query: string,
): ClaudeModelEntry[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return [...entries];
  return entries.filter(
    (entry) =>
      entry.displayName.toLowerCase().includes(needle) ||
      entry.id.toLowerCase().includes(needle),
  );
}

function findExactEntry(query: string): ClaudeModelEntry | undefined {
  const needle = query.trim().toLowerCase();
  if (!needle) return undefined;
  return ALL_MODELS.find(
    (entry) =>
      entry.id.toLowerCase() === needle ||
      entry.displayName.toLowerCase() === needle,
  );
}

function isExactMatch(query: string): boolean {
  return findExactEntry(query) !== undefined;
}

/**
 * A searchable, grouped picker for the model an LLM Profile runs under
 * (spec 086) — a drop-in replacement for the plain text `Input` this field
 * used before: any string is still accepted (FR-006), this only adds a way
 * to find and pick a known one without typing it from memory.
 *
 * Built on the same combobox primitives as `AssigneePicker`/`LabelPicker`.
 * Unlike those, the input itself shows the current value's friendly name
 * (via `inputValue`), because this field's value is always meaningful and
 * required — there is no separate "nothing chosen" state to keep the input
 * free for searching, the way an optional assignee has.
 */
export function ClaudeModelCombobox({
  value,
  onChange,
  id,
  disabled = false,
  autoFocus,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
}: {
  value: string;
  onChange: (model: string) => void;
  id?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
}) {
  const [query, setQuery] = useState(() => claudeModelName(value) ?? value);

  const filteredTiers = useMemo(
    () =>
      CLAUDE_MODEL_CATALOG.map((tier) => ({
        ...tier,
        models: matches(tier.models, query),
      })).filter((tier) => tier.models.length > 0),
    [query],
  );

  const trimmedQuery = query.trim();
  const showFreeTextOption = trimmedQuery !== '' && !isExactMatch(query);

  function commit(model: string) {
    onChange(model);
    setQuery(claudeModelName(model) ?? model);
  }

  return (
    <Combobox<string, false>
      value={value}
      onValueChange={(next) => {
        commit(next ?? '');
      }}
      inputValue={query}
      onInputValueChange={(next) => {
        setQuery(next);
        // Typing alone commits the typed text as the value, same as the plain
        // `Input` this replaces (FR-006, FR-010) — selecting a list item is a
        // convenience for finding a known id, not the only way to set one.
        // When the typed text exactly matches a known entry (including via the
        // primitive's own inline autocomplete fill), commit that entry's real
        // id rather than the friendly-name text sitting in the input.
        onChange(findExactEntry(next)?.id ?? next);
      }}
      items={[
        ...filteredTiers.flatMap((tier) => tier.models.map((m) => m.id)),
        ...(showFreeTextOption ? [query] : []),
      ]}
      itemToStringLabel={(modelId) => claudeModelName(modelId) ?? modelId}
      disabled={disabled}
    >
      <ComboboxInputGroup>
        <ComboboxInput
          id={id}
          placeholder="claude-opus-4-8"
          className="font-mono"
          autoFocus={autoFocus}
          aria-invalid={ariaInvalid}
          aria-describedby={ariaDescribedBy}
        />
      </ComboboxInputGroup>

      <ComboboxContent>
        <ComboboxList>
          {filteredTiers.map((tier) => (
            <ComboboxGroup key={tier.tier} items={tier.models.map((m) => m.id)}>
              <ComboboxGroupLabel>
                {tier.tier}
                <Badge variant="secondary">{tier.efficiency}</Badge>
              </ComboboxGroupLabel>
              {tier.models.map((entry) => (
                <ComboboxItem key={entry.id} value={entry.id}>
                  <span className="flex min-w-0 flex-col leading-tight">
                    <span className="truncate">
                      {entry.displayName}
                      {entry.deprecated ? (
                        <span className="text-muted-foreground ml-1.5 text-xs">
                          Deprecated
                        </span>
                      ) : null}
                    </span>
                    <span className="text-muted-foreground truncate font-mono text-xs">
                      {entry.id}
                    </span>
                  </span>
                </ComboboxItem>
              ))}
            </ComboboxGroup>
          ))}

          {showFreeTextOption ? (
            <ComboboxItem value={query}>
              <span className="truncate">Use &ldquo;{query}&rdquo;</span>
            </ComboboxItem>
          ) : null}
        </ComboboxList>

        {filteredTiers.length === 0 && !showFreeTextOption ? (
          <ComboboxEmpty>
            <span className="text-muted-foreground text-sm">
              No model matches &ldquo;{trimmedQuery}&rdquo;.
            </span>
          </ComboboxEmpty>
        ) : null}
      </ComboboxContent>
    </Combobox>
  );
}
